import { useState } from "react"
import { useLocation } from "wouter"
import { Button } from "../../../components/Button"
import { FeedbackLayout } from "../../../components/FeedbackLayout"
import { InputField } from "@rawa/ui/InputField"
import typography from "@rawa/ui/styles/typography.module.css"
import { postChallenge } from "../api"
import { pathHelpers } from "../../../paths"
import styles from "./IdentityChallengePage.module.scss"

// One message for every refusal — naming the field would confirm who is on a
// roster.
const REFUSED = "We could not match those details. Please ask your facilitator."

// Where a QR code lands: the participant gives a name instead of picking one off
// a roster. The name is sent unaltered — normalising is the
// server's job — and nothing here names a session, because the printed code does
// not carry one.
export function IdentityChallengePage() {
  const [, navigate] = useLocation()
  const [firstName, setFirstName] = useState("")
  const [surname, setSurname] = useState("")
  // Set once the server asks, which it does when the first name alone describes
  // more than one person.
  const [surnameNeeded, setSurnameNeeded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [missing, setMissing] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault()
    // Naming an empty field is safe: it says what the participant has not done,
    // nothing about who is on the roster.
    if (!firstName.trim() || (surnameNeeded && !surname.trim())) {
      setMissing(true)
      return
    }
    setMissing(false)
    setSubmitting(true)
    setError(null)
    try {
      const result = await postChallenge({
        firstName,
        surname: surnameNeeded ? surname : undefined,
      })
      if (result.outcome === "surname-needed") {
        setSurnameNeeded(true)
        setSubmitting(false)
        return
      }
      // Their own form, the same page an SMS link or a kiosk mint arrives at.
      // The button stays disabled through the navigation.
      navigate(pathHelpers.toFeedbackWelcome(result.invitationId))
    } catch {
      setError(REFUSED)
      setSubmitting(false)
    }
  }

  return (
    <FeedbackLayout>
      <h1 className={styles.pageHeading}>Give your feedback</h1>
      <div className={styles.container}>
        <form className={styles.card} onSubmit={onSubmit} noValidate>
          <p className={typography.bodyLarge}>
            Confirm who you are and we will bring up your feedback form.
          </p>

          {/* The first name is settled once it has been accepted, and is still
              sent with the surname — leaving the field on screen would invite
              editing an answer the second question was chosen for. */}
          {!surnameNeeded && (
            <InputField
              id="firstName"
              label="First name"
              value={firstName}
              onChange={setFirstName}
              autoComplete="given-name"
              error={
                missing && !firstName.trim()
                  ? "Enter your first name"
                  : undefined
              }
            />
          )}

          {surnameNeeded && (
            <InputField
              id="surname"
              label="Last name"
              value={surname}
              onChange={setSurname}
              autoComplete="family-name"
              error={
                missing && !surname.trim() ? "Enter your last name" : undefined
              }
            />
          )}

          {error && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}

          <Button type="submit" disabled={submitting}>
            Continue
          </Button>
        </form>
      </div>
    </FeedbackLayout>
  )
}
